import React, { Suspense, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout, { AdminPageEmbedContext } from '../../../components/layout/DashboardLayout';
import { useLanguage } from '../../../context/LanguageContext';

const TABS = [
  { id: 'features', labelKey: 'sidebar.admin.vehicleFeatures', loader: () => import('./VehicleFeatures') },
  { id: 'types', labelKey: 'sidebar.admin.vehicleType', loader: () => import('./VehicleTypes') },
  { id: 'locations', labelKey: 'sidebar.admin.vehicleLocation', loader: () => import('./VehicleLocations') },
  { id: 'makes', labelKey: 'sidebar.admin.vehicleMakes', loader: () => import('./VehicleMakes') },
  { id: 'models', labelKey: 'sidebar.admin.vehicleModel', loader: () => import('./VehicleModels') },
  { id: 'odometer', labelKey: 'sidebar.admin.vehicleOdometer', loader: () => import('./VehicleOdometer') },
  { id: 'fuel-types', labelKey: 'sidebar.admin.vehicleFuelType', loader: () => import('./VehicleFuelTypes') },
  { id: 'payout-methods', labelKey: 'sidebar.admin.payoutMethod', loader: () => import('./PayoutMethods') },
  { id: 'rules', labelKey: 'sidebar.admin.vehicleRules', loader: () => import('./VehicleRules') },
  { id: 'cancellation-policies', labelKey: 'sidebar.admin.cancellationPolicies', loader: () => import('./CancellationPolicies') },
  { id: 'cancellation-reasons', labelKey: 'sidebar.admin.cancellationReason', loader: () => import('./CancellationReasons') },
  { id: 'rejection-reasons', labelKey: 'sidebar.admin.rejectionReasons', loader: () => import('./RejectionReasons') },
];

const TAB_COMPONENTS = Object.fromEntries(
  TABS.map((tab) => [tab.id, React.lazy(tab.loader)])
);

const VehicleSettings = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = TABS.some((tab) => tab.id === requestedTab) ? requestedTab : TABS[0].id;
  const ActiveComponent = TAB_COMPONENTS[activeTab];

  const title = useMemo(
    () => t('sidebar.admin.vehicleSettings') || 'Paramètres / Configurations',
    [t]
  );

  const selectTab = (tabId) => {
    setSearchParams({ tab: tabId }, { replace: true });
  };

  return (
    <DashboardLayout>
      <div className="pl-6 space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500 mt-1">
            Données de référence utilisées pour la création et la validation des véhicules.
          </p>
        </div>

        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="Onglets paramètres véhicules">
            {TABS.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => selectTab(tab.id)}
                  className={`
                    whitespace-nowrap px-3 py-2 text-sm font-medium border-b-2 transition-colors
                    ${isActive
                      ? 'border-[#27489E] text-[#27489E]'
                      : 'border-transparent text-gray-500 hover:text-[#27489E] hover:border-gray-300'
                    }
                  `}
                >
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </nav>
        </div>

        <AdminPageEmbedContext.Provider value={true}>
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-[#27489E] border-t-transparent rounded-full animate-spin" />
              </div>
            }
          >
            <ActiveComponent />
          </Suspense>
        </AdminPageEmbedContext.Provider>
      </div>
    </DashboardLayout>
  );
};

export default VehicleSettings;
